import io from "socket.io-client";

export const SOCKET_RECONNECT_CONFIG = Object.freeze({
  reconnection: true,
  reconnectionAttempts: 20,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 10000,
  randomizationFactor: 0.5,
  timeout: 20000,
});

/**
 * Socket Manager Class
 * Handles socket.io connections with queue system for offline operations
 */
export class Socket {
  constructor({ url, project, accessToken }) {
    this.url = url;
    this.project = project;
    this.accessToken = accessToken;
    this.queue = [];
    this.socket = null;
    this.isConnected = false;
  }

  /**
   * Initialize socket connection
   */
  connect() {
    if (this.socket?.connected) {
      return this;
    }

    this.socket = io(this.url, {
      extraHeaders: {
        Authorization: `Bearer ${this.accessToken}`,
      },
      query: `token=${this.accessToken}&project=${this.project}`,
      transports: ["websocket"],
      autoConnect: true,
      ...SOCKET_RECONNECT_CONFIG,
    });

    this.socket.on("connect", () => {
      this.isConnected = true;

      // Process queued operations
      this.processQueue();
    });

    this.socket.on("disconnect", () => {
      this.isConnected = false;
    });

    return this;
  }

  /**
   * Get socket ID
   */
  getId() {
    return this.socket?.id;
  }

  /**
   * Close socket connection
   */
  close() {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.isConnected = false;
    }
  }

  /**
   * Join a room
   */
  join(room) {
    if (this.socket?.connected) {
      const paramRoom =
        typeof room === "object"
          ? { ...room, project: this.project }
          : `${this.project}_${room}`;

      this.socket.emit("join", paramRoom);
    } else {
      this.queue.push({ action: "join", room });
    }
    return this;
  }

  /**
   * Leave a room
   */
  leave(room) {
    if (this.socket?.connected) {
      const paramRoom =
        typeof room === "object"
          ? { ...room, project: this.project }
          : `${this.project}_${room}`;

      this.socket.emit("leave", paramRoom);
    } else {
      this.queue.push({ action: "leave", room });
    }
    return this;
  }

  /**
   * Listen to an event
   */
  on(event, func = () => {}) {
    if (this.socket?.connected) {
      this.socket.on(event, func);
    } else {
      this.queue.push({ action: "on", event, func });
    }
    return this;
  }

  /**
   * Remove event listener
   */
  off(event, func = () => {}) {
    if (this.socket?.connected) {
      this.socket.off(event, func);
    } else {
      this.queue.push({ action: "off", event, func });
    }
    return this;
  }

  /**
   * Emit an event
   */
  emit(event, data) {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn("Socket not connected, cannot emit event:", event);
    }
    return this;
  }

  /**
   * Process queued operations when socket connects
   */
  processQueue() {
    let item;
    while ((item = this.queue.shift()) !== undefined) {
      switch (item.action) {
        case "on":
          this.socket.on(item.event, item.func);
          break;
        case "join":
          this.join(item.room);
          break;
        case "leave":
          this.leave(item.room);
          break;
        case "off":
          this.socket.off(item.event, item.func);
          break;
        default:
          console.warn("Unknown queue action:", item.action);
      }
    }
  }

  /**
   * Check if socket is connected
   */
  get connected() {
    return this.socket?.connected || false;
  }
}

export default Socket;
